"use client";

import Link from "next/link";
import type { ComponentProps } from "react";
import { usePathname } from "next/navigation";

import { SOCIAL_LINKS } from "@/lib/nav-data";
import { cn } from "@/lib/utils";

const Footer = ({ ...rest }: ComponentProps<"footer">) => {
  const pathname = usePathname();

  return (
    <footer
      className={cn(
        "pt-6 relative overflow-hidden z-0",
        {
          ["bg-white"]: pathname.startsWith("/partner"),
        },
        rest.className
      )}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-12 gap-4 md:gap-6">
        {/* Desktop layout: inner 10 columns, 4 content groups */}
        <div className="hidden md:col-span-12 md:grid grid-cols-4 gap-6">
          {/* Socials */}
          <div className="flex flex-col gap-1">
            <p className="text-copy-sm text-gray-75">Socials</p>
            <div className="flex gap-2 items-center">
              {SOCIAL_LINKS.map(({ href, label, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                >
                  <Icon className="size-9 text-black" />
                </a>
              ))}
            </div>
          </div>

          {/* Angebot */}
          <div className="flex flex-col gap-1">
            <p className="text-copy-sm text-gray-75">Angebot</p>
            <ul className="text-link-lg">
              <li>
                <Link href="/membership" className="hover:underline">
                  Membership
                </Link>
              </li>
              <li>
                <Link href="/events" className="hover:underline">
                  Events
                </Link>
              </li>
              <li>
                <Link href="/events/reisen" className="hover:underline">
                  Reisen
                </Link>
              </li>
              <li>
                <Link href="/newsletter" className="hover:underline">
                  Newsletter
                </Link>
              </li>
            </ul>
          </div>

          {/* Dialetics */}
          <div className="flex flex-col gap-1">
            <p className="text-copy-sm text-gray-75">Dialetics</p>
            <ul className="text-link-lg">
              <li>
                <Link href="/about" className="hover:underline">
                  Über uns
                </Link>
              </li>
              <li>
                <Link href="/karriere" className="hover:underline">
                  Karriere
                </Link>
              </li>
              <li>
                <Link href="/kontakt" className="hover:underline">
                  Kontakt
                </Link>
              </li>
              <li>
                <Link href="/presse" className="hover:underline">
                  Presse
                </Link>
              </li>
              <li>
                <Link href="/praxen" className="hover:underline">
                  Partner-Praxen
                </Link>
              </li>
              <li>
                <Link href="/partner/b2b" className="hover:underline">
                  B2B
                </Link>
              </li>
            </ul>
          </div>

          {/* Legals */}
          <div className="flex flex-col gap-1">
            <p className="text-copy-sm text-gray-75">Legals</p>
            <ul className="text-label">
              <li>
                <Link href="/impressum" className="hover:underline">
                  Impressum
                </Link>
              </li>
              <li>
                <Link href="/datenschutz" className="hover:underline">
                  Datenschutz
                </Link>
              </li>
              <li>
                <Link href="/widerruf" className="hover:underline">
                  Widerruf
                </Link>
              </li>
              <li>
                <Link href="/agb" className="hover:underline">
                  AGB
                </Link>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => (window as any).Cookiebot?.renew()}
                  className="hover:underline"
                >
                  Cookie-Einstellungen
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Mobile layout: 2 columns */}
        <div className="col-span-12 grid grid-cols-[2fr_1fr] gap-6 md:hidden">
          {/* Left column: Angebot + Socials */}
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <p className="text-copy-sm text-gray-75">Angebot</p>
              <ul className="text-link-lg">
                <li>
                  <Link href="/login" className="hover:underline">
                    Membership
                  </Link>
                </li>
                <li>
                  <Link href="/events" className="hover:underline">
                    Events
                  </Link>
                </li>
                <li>
                  <Link href="/events/reisen" className="hover:underline">
                    Reisen
                  </Link>
                </li>
                <li>
                  <Link href="/newsletter" className="hover:underline">
                    Newsletter
                  </Link>
                </li>
              </ul>
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-copy-sm text-gray-75">Socials</p>
              <div className="flex gap-2 items-center">
              {SOCIAL_LINKS.map(({ href, label, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                >
                  <Icon className="size-9 text-black" />
                </a>
              ))}
            </div>
            </div>
          </div>

          {/* Right column: Dialetics + Legals */}
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <p className="text-copy-sm text-gray-75">Dialetics</p>
              <ul className="text-link-lg">
                <li>
                  <Link href="/about" className="hover:underline">
                    Über uns
                  </Link>
                </li>
                <li>
                  <Link href="/karriere" className="hover:underline">
                    Karriere
                  </Link>
                </li>
                <li>
                  <Link href="/kontakt" className="hover:underline">
                    Kontakt
                  </Link>
                </li>
                <li>
                  <Link href="/presse" className="hover:underline">
                    Presse
                  </Link>
                </li>
                <li>
                  <Link href="/partner/hcp" className="hover:underline">
                    HCP
                  </Link>
                </li>
                <li>
                  <Link href="/partner/b2b" className="hover:underline">
                    B2B
                  </Link>
                </li>
              </ul>
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-copy-sm text-gray-75">Legals</p>
              <ul className="text-label">
                <li>
                  <Link href="/impressum" className="hover:underline">
                    Impressum
                  </Link>
                </li>
                <li>
                  <Link href="/datenschutz" className="hover:underline">
                    Datenschutz
                  </Link>
                </li>
                <li>
                  <Link href="/widerruf" className="hover:underline">
                    Widerruf
                  </Link>
                </li>
                <li>
                  <Link href="/agb" className="hover:underline">
                    AGB
                  </Link>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => (window as any).Cookiebot?.renew()}
                    className="hover:underline"
                  >
                    Cookie-Einstellungen
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Shapes */}
      <div className="mt-6 w-full h-[216px] overflow-hidden">
        <div className="bg-footer-pattern bg-repeat-x bg-bottom w-full h-full bg-[length:auto_100%]" />
      </div>
    </footer>
  );
};

export { Footer };
